import { DateTime } from 'luxon';
import { ObjectSchema, ValidationError } from 'yup';
import { colors } from 'constants-colors';
import type { WorkOrder } from 'integrations-vantify-client-services';

export function simpleDateTime(date: Date | string, timeZone?: string) {
  if (!date) return '';

  return DateTime.fromISO(
    typeof date === 'string' ? date : date.toISOString(),
    { zone: timeZone || 'local' }
  ).toFormat('MMM d, yyyy h:mm a');
}

export type WorkOrderAttachment = {
  id?: string;
  file_association_id?: string;
  display_name: string;
  thumbnail?: string;
  created_at?: string;
  category?: {
    name: string;
  };
}

const STATUS_COLOR_MAP: Record<string, string> = {
  open: colors.laneGreen,
  created: colors.interactiveBlue,
  approval_pending: colors.laneGreen,
  approval_rejected: colors.honey,
  assignment_pending: colors.functionalCritical,
  acknowledged: colors.laneGreen,
  commenced: colors.neutral.grey900,
  missed: colors.functionalCritical,
  cancelled: colors.functionalCritical,
  completed: colors.laneGreen,
  awaiting_paperwork: colors.honey,
  paperwork_rejected: colors.functionalCritical,
  extension_pending: colors.honey,
  assignment_proposal: colors.neutral.grey900,
  assignment_rejected: colors.neutral.grey900,
  operative_assignment_rejected: colors.neutral.grey900,
};

export const STATUS_LABEL_MAP: Record<string, string> = {
  open: 'Open',
  created: 'Created',
  approval_pending: 'Pending Approval',
  approval_rejected: 'Approval Rejected',
  assignment_pending: 'Pending Acknowledgement',
  assignment_proposal: 'SLA Proposal Pending',
  assignment_rejected: 'Assignment Rejected',
  operative_assignment_rejected: 'Job Operative Assignment Rejected',
  acknowledged: 'Acknowledged',
  commenced: 'In Progress',
  missed: 'Missed',
  cancelled: 'Cancelled',
  completed: 'Completed',
  awaiting_paperwork: 'Awaiting Paperwork',
  paperwork_rejected: 'Paperwork Rejected',
  extension_pending: 'Extension Pending',
};

export function getStatusColor(status: string): string {
  const color = STATUS_COLOR_MAP[status];

  return color ?? colors.neutral.grey900;
}

export function getStatusColorValue(colorName: string): string {
  const colorMap: Record<string, string> = {
    blue: colors.interactiveBlue,
    green: colors.laneGreen,
    honey: colors.honey,
    red: colors.functionalCritical,
    gray: colors.neutral.grey900,
  };

  return colorMap[colorName] || colors.neutral.grey900;
}

export type DropdownOption = {
  id: string;
  name: string;
};

export type ValidationResult = {
  messages: string[];
  success: boolean;
  error: boolean;
};

export async function validatedControl<T extends object | null | undefined>(
  value: T,
  schema: ObjectSchema<T>
): Promise<ValidationResult> {
  return schema
    .validate(value, { abortEarly: false })
    .then(() => ({
      messages: [],
      success: true,
      error: false,
    }))
    .catch((err: ValidationError) => ({
      messages: err.errors ?? [],
      success: false,
      error: true,
    }));
}

const nonCancellableStatuses = ['cancelled', 'completed'];

export const isWorkOrderCancellable = (activeWorkOrder?: WorkOrder | null) => {
  if (!activeWorkOrder?.status) return false;

  const normalizedStatus = activeWorkOrder.status.toLowerCase();

  return !nonCancellableStatuses.includes(normalizedStatus);
};